/**
 * Classement des CLUBS au Mondial : note moyenne des joueurs d'un même club
 * présents en Coupe du monde (moyenne de leur note-tournoi), + buts et passes
 * décisives CUMULÉS de ces joueurs (agrégés côté serveur depuis l'archive —
 * cf. server/leaderboards.mjs). Répond à « quels clubs brillent via leurs
 * internationaux ? ». Table à TRI DEUX NIVEAUX (cf. tableSort.ts) ; seuls les
 * clubs comptant ≥ 3 joueurs notés figurent.
 */
import type { ClubRating } from "../../app/leaderboards.ts";
import { esc } from "../html.ts";
import { t } from "../../app/i18n.ts";
import { openClubDetail } from "../clubDetail.ts";
import { createSortState, cycleSort, sortRows, sortClass, sortMark, sortAria } from "./tableSort.ts";

type ClubSortKey = "note" | "goals" | "assists" | "players";
const sort = createSortState<ClubSortKey>("note");
let lastEl: HTMLElement | null = null;
let lastRows: readonly ClubRating[] = [];

/** Y a-t-il de quoi afficher la section ? (sinon on la masque — règle 8) */
export function hasClubRatings(rows: readonly ClubRating[] | undefined): boolean {
  return Array.isArray(rows) && rows.length > 0;
}

const tip = (c: ClubRating): string =>
  c.roster
    .slice(0, 5)
    .map((p) => `${p.name ?? "—"}${p.teamCode ? ` (${p.teamCode})` : ""} ${p.rating.toFixed(1)}`)
    .join(" · ");

const metric = (c: ClubRating, k: ClubSortKey): number =>
  k === "goals" ? c.goals : k === "assists" ? c.assists : k === "players" ? c.players : c.rating;

function applySort(key: ClubSortKey): void {
  cycleSort(sort, key);
  if (lastEl) renderClubRatings(lastEl, lastRows);
}

export function renderClubRatings(el: HTMLElement, rows: readonly ClubRating[]): void {
  lastEl = el;
  lastRows = rows;
  if (!hasClubRatings(rows)) {
    el.innerHTML = "";
    el.classList.add("hide");
    return;
  }
  el.classList.remove("hide");

  const sorted = sortRows(rows, sort, metric, (a, b) => a.club.localeCompare(b.club));
  const th = (k: ClubSortKey, label: string, title: string): string => {
    const cls = sortClass(sort, k);
    return `<th class="cr-th-sort${cls ? ` ${cls}` : ""}" data-sort="${k}" role="button" tabindex="0" aria-sort="${sortAria(sort, k)}" title="${esc(title)}">${label}${sortMark(sort, k)}</th>`;
  };

  const body = sorted
    .map((c, i) => {
      const logo = c.clubLogo
        ? `<img class="cr-logo" src="${esc(c.clubLogo)}" alt="" loading="lazy" decoding="async" width="20" height="20">`
        : `<span class="cr-logo cr-logo-ph" aria-hidden="true"><i class="ti ti-shield"></i></span>`;
      return `
      <tr class="cr-row cr-click" data-club="${esc(c.club)}" role="button" tabindex="0" title="${esc(t("clubs.row.title", { name: c.club, tip: tip(c) }))}">
        <td class="cr-rank">${i + 1}</td>
        <td class="cr-club">${logo}<span class="cr-name">${esc(c.club)}</span></td>
        <td class="cr-note"><span class="cr-num">${c.rating.toFixed(2)}</span></td>
        <td class="cr-stat">${c.goals}</td>
        <td class="cr-stat">${c.assists}</td>
        <td class="cr-count">${c.players}</td>
      </tr>`;
    })
    .join("");

  el.innerHTML = `
    <h2 class="sec-title">${t("clubs.section.title")} <span class="count">${t("clubs.section.count")}</span></h2>
    <div class="card cr-card">
      <table class="cr-table">
        <thead>
          <tr>
            <th class="cr-th-rank" scope="col">#</th>
            <th class="cr-th-club" scope="col">${t("clubs.col.club")}</th>
            ${th("note", t("clubs.col.avgRating"), t("clubs.col.avgRating.title"))}
            ${th("goals", t("stats.col.goals"), t("clubs.col.goals.title"))}
            ${th("assists", t("clubs.col.assists"), t("clubs.col.assists.title"))}
            ${th("players", t("stats.section.players"), t("clubs.col.players.title"))}
          </tr>
        </thead>
        <tbody>${body}</tbody>
      </table>
      <p class="note">${t("clubs.note")}</p>
    </div>`;

  // Clic : en-tête → tri ; ligne club → modale détail par joueur.
  const dispatch = (t: HTMLElement): boolean => {
    const th = t.closest<HTMLElement>("[data-sort]");
    if (th?.dataset.sort) {
      applySort(th.dataset.sort as ClubSortKey);
      return true;
    }
    const row = t.closest<HTMLElement>(".cr-row[data-club]");
    const club = row?.dataset.club ? lastRows.find((c) => c.club === row.dataset.club) : null;
    if (club) {
      openClubDetail(club);
      return true;
    }
    return false;
  };
  el.onclick = (e) => void dispatch(e.target as HTMLElement);
  el.onkeydown = (e) => {
    if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") return;
    if (dispatch(e.target as HTMLElement)) e.preventDefault();
  };
}
